<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('agents.agentsLabel') }}</span>
        <h3 class="page-title">{{ $t('agents.editAgent') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/agenci')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">{{ $t('ui.saveChanges') }}</d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer
              v-if="loaded"
              ref="agent-edit-form"
              tag="form">
              <div class="form-group">
                <label for="name">
                  {{ $t('ui.name') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('agents.commission') }}:
                </label>

                <validation-provider
                  rules="required|min_value:0|max_value:100"
                  name="commission"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    :min="0"
                    :step="0.01"
                    :max="100"
                    v-model="commission" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <d-row>
                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('agents.sendReport') }}:
                    </label>

                    <div>
                      <d-checkbox
                        inline
                        v-model="send_report"
                        toggle
                        toggle-small />
                    </div>
                  </div>
                </d-col>
                <d-col
                  v-if="send_report"
                  cols="10">
                  <div class="form-group">
                    <label>
                      {{ $t('agents.reportEmail') }}:
                    </label>

                    <validation-provider
                      rules="email"
                      name="email"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        type="email"
                        v-model="report_email"
                        :readonly="!send_report" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </validation-observer>

            <div v-if="!loaded" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab
            :title="$t('agents.assignedCustomers')">
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('agents.assignedCustomers') }}
            </h4>

            <customers
              v-if="loaded"
              :agentID="id" />
          </d-tab>

          <d-tab
            :title="$t('agents.commissionsList')">
            <agent-commissions
              v-if="loaded"
              :agentID="id" />
          </d-tab>

          <d-tab
            :title="$t('agents.settlementsHistory')">
            <h4 class="mb-3 pb-3 border-bottom d-flex">
              <span class="d-block">
                {{ $t('agents.settlementsHistory') }}
              </span>

              <d-button
                theme="success"
                @click.prevent="exportSettlements"
                :disabled="exportInProgress"
                class="ml-auto">
                <div
                  v-if="exportInProgress"
                  class="spinner-border spinner-border-sm"
                  role="status"></div>
                {{ $t('agents.settlements.export') }}
              </d-button>
            </h4>

            <agent-settlements
              v-if="loaded"
              :agentID="id" />
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import AgentCommissions from './AgentCommissions.vue';
import AgentSettlements from './AgentSettlements.vue';
import Customers from './../../views/Customers.vue';
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'agent-edit',
  components: {
    'agent-commissions': AgentCommissions,
    'agent-settlements': AgentSettlements,
    'customers': Customers
  },
  data() {
    return {
      id: 0,
      name: '',
      commission: '',
      send_report: false,
      report_email: '',
      // flags
      loaded: false,
      loadError: false,
      exportInProgress: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/agents/item/',
      instanceFields: ['name', 'commission', 'send_report', 'report_email'],
      booleanFields: ['send_report'],
      loadedKey: 'loaded'
    });
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['agent-edit-form'], this.submitForm);
    },
    submitForm () {
      let dataToSend = {
        name: this.name,
        commission: this.commission,
        send_report: +this.send_report
      };

      if (this.send_report) {
        dataToSend.report_email = this.report_email;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/agents/update/' + this.id,
        formData: dataToSend,
        successTitle: this.$t('agents.editAgentSuccessTitle'),
        successText: this.$t('agents.editAgentSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('agents.editAgentErrorText'),
        successAction: () => {
          this.$router.push('/agenci')
        }
      });
    },
    exportSettlements () {
      this.exportInProgress = true;

      this.$http.post('/agents/settlements/export', {
        where: {
          agent_id: this.id
        }
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.exportInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.exportErrorTitle'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.exportInProgress = false;
      });
    }
  }
};
</script>
